import { DatePicker, Flex, Segmented } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getLocale } from '../../common/components/datePickerHelpers';
import { spacing } from '../../theming';
import { MAX_RANGE_DAYS, RANGE_PRESETS, formatLocal } from '../../utils/vinExtraction';
import { labelStyle } from './vinExtractionStyles';

// the year only when the edge is not in the current one
const formatEdge = (value, lng) =>
    formatLocal(value, value.year() === dayjs().year() ? 'D MMM HH:mm' : 'D MMM YYYY HH:mm', lng);

// A custom range covers whole local hours (SC-QA-104): from the start of the first hour to the end of the last,
// both picked hours included, and never past now: a start in a later hour today moves back to this hour, so the
// range is never empty. The picker starts on 00 and ends on 23, so picking only days still gives whole days.
export const customRange = ([start, end]) => {
    const now = dayjs();
    const from = start.isAfter(now.startOf('hour')) ? now.startOf('hour') : start.startOf('hour');
    const last = end.isBefore(from) ? from : end.startOf('hour');
    const to = last.add(1, 'hour');

    return { from, to: to.isAfter(now) ? now : to };
};

// The picker shows the last hour the range includes, not its end (the hour after), so opening it and pressing OK
// again keeps the same range instead of adding an hour each time.
export const pickerValue = range => [range.from, range.to.subtract(1, 'millisecond').startOf('hour')];

// the hour picker's starting values: the whole first day to the whole last day
const DEFAULT_HOURS = [dayjs().startOf('day'), dayjs().startOf('day').hour(23)];

const disabledDate = (current, { from }) =>
    current.isAfter(dayjs(), 'day') || (from ? Math.abs(current.diff(from, 'day')) >= MAX_RANGE_DAYS : false);

const RangeControls = ({ range, onRangeChange }) => {
    const { t, i18n } = useTranslation('vinExtraction');
    const options = [...RANGE_PRESETS, 'custom'].map(value => ({ value, label: t(`range.presets.${value}`) }));

    return (
        <Flex align="center" gap={spacing[2]} wrap="wrap">
            <Segmented
                aria-label={t('range.aria')}
                options={options}
                value={range.preset}
                onChange={preset => onRangeChange({ preset })}
            />
            {range.preset === 'custom' && (
                <DatePicker.RangePicker
                    aria-label={t('range.pickerAria')}
                    locale={getLocale(i18n.language)}
                    format="DD.MM.YYYY HH:00"
                    showTime={{ format: 'HH', defaultValue: DEFAULT_HOURS }}
                    allowClear={false}
                    value={pickerValue(range)}
                    disabledDate={disabledDate}
                    onChange={dates =>
                        dates?.[0] && dates?.[1] && onRangeChange({ preset: 'custom', ...customRange(dates) })
                    }
                />
            )}
            <span style={labelStyle} data-testid="vin-range-text">
                {t(`range.labels.${range.preset}`)} · {formatEdge(range.from, i18n.language)} →{' '}
                {formatEdge(range.to, i18n.language)}
            </span>
        </Flex>
    );
};

export default RangeControls;
